import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useB2BTranslation } from '../../B2BShared/hooks/useB2BTranslation';
import { useB2BDataLoadingState, useB2BFormLoadingState } from '../../B2BShared/hooks/useB2BLoadingState';
import { ArrowLeft, Edit, Save, X } from 'lucide-react';
import { useB2BAuth } from '../../B2BShared';
import { api } from '../../../../utils/apiConfig';
import { translatePosition } from '../../B2BShared/utils/translatePosition';
import './B2BAdminProfileView.css';

interface AdminProfileViewProps {
  userId?: string;
}

const B2BAdminProfileView: React.FC<AdminProfileViewProps> = () => {
  const { t, tDashboard, tAuth, tBusinessCategory, tDashboardExists } = useB2BTranslation();
  const { user } = useB2BAuth();
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  
  const [profileData, setProfileData] = useState<any>(null);
  const [isEditingNotes, setIsEditingNotes] = useState(false);
  const [adminNotes, setAdminNotes] = useState('');
  
  const {
    isLoading,
    fetchData
  } = useB2BDataLoadingState();
  
  const {
    isLoading: isSaving,
    handleSubmit: handleSaveSubmit
  } = useB2BFormLoadingState();

  const loadUserProfile = useCallback(async () => {
    await fetchData(async () => {
      // Debug authentication
      const token = localStorage.getItem('b2bToken');
      console.log('Auth token available:', !!token);
      console.log('User context:', user);
      console.log('User ID to fetch:', userId);
      
      // This would be a new API endpoint to get user profile by ID for admins
      const response = await api.getUserProfileById(userId!);
      // Handle the nested response structure from backend
      const userData = (response as any).data?.user || response.data;
      setProfileData(userData);
      setAdminNotes(userData.adminNotes || '');
    }).catch((error) => {
      console.error('Error loading user profile:', error);
    });
  }, [userId, user, fetchData]);

  useEffect(() => {
    if (userId) {
      loadUserProfile();
    }
  }, [userId, loadUserProfile]);

  // Check if current user is admin
  if (!user?.isAdmin) {
    return (
      <div className="admin-profile-error">
        <h2>{t('admin.accessDenied')}</h2>
        <p>{t('admin.accessRequired')}</p>
        <button 
          className="b2b-button b2b-button--primary"
          onClick={() => navigate('/gcb/admin/dashboard')}
        >
          {t('admin.backToDashboard')}
        </button>
      </div>
    );
  }


  const handleSaveAdminNotes = async () => {
    await handleSaveSubmit(async () => {
      await api.updateUserAdminNotes(userId!, adminNotes);
      setProfileData(prev => ({ ...prev, adminNotes }));
      setIsEditingNotes(false);
    }).catch((error) => {
      console.error('Error saving admin notes:', error);
    });
  };

  const handleCancelEdit = () => {
    setAdminNotes(profileData?.adminNotes || '');
    setIsEditingNotes(false);
  };

  if (isLoading) {
    return (
      <div className="admin-profile-loading">
        <div className="loading-spinner"></div>
        <p>{t('admin.loadingProfile')}</p>
      </div>
    );
  }

  if (!profileData) {
    return (
      <div className="admin-profile-error">
        <h2>{t('admin.profileNotFound')}</h2>
        <p>{t('admin.profileNotFoundMessage')}</p>
        <button 
          className="b2b-button b2b-button--primary"
          onClick={() => navigate('/gcb/admin/dashboard')}
        >
          {t('admin.backToDashboard')}
        </button>
      </div>
    );
  }

  return (
    <div className="admin-profile-container">
      {/* Header */}
      <div className="admin-profile-header">
        <button 
          className="b2b-button b2b-button--secondary"
          onClick={() => navigate('/gcb/admin/dashboard')}
        >
          <ArrowLeft size={16} />
          {t('admin.backToDashboard')}
        </button>
        
        <div className="admin-profile-title">
          <h1>{t('admin.profileView')}</h1>
          <p>{t('admin.viewingProfileFor', { name: profileData.fullName || profileData.name || 'Unknown User' })}</p>
        </div>
        
        {/* Spacer to balance the layout */}
        <div className="admin-profile-header-spacer"></div>
      </div>

      {/* Admin Notes Section */}
      <div className="admin-notes-section">
        <div className="admin-notes-header">
          <div className="admin-notes-title">
            <span className="admin-icon">🔒</span>
            <h2>{t('admin.notes')}</h2>
          </div>
          
          {!isEditingNotes ? (
            <button 
              className="b2b-button b2b-button--primary"
              onClick={() => setIsEditingNotes(true)}
            >
              <Edit size={16} />
              {t('admin.editNotes')}
            </button>
          ) : (
            <div className="admin-notes-actions">
              <button 
                className="b2b-button b2b-button--success"
                onClick={handleSaveAdminNotes}
                disabled={isSaving}
              >
                <Save size={16} />
                {isSaving ? t('dashboard.common.saving') : t('dashboard.common.save')}
              </button>
              <button 
                className="b2b-button b2b-button--secondary"
                onClick={handleCancelEdit}
                disabled={isSaving}
              >
                <X size={16} />
                {t('dashboard.common.cancel')}
              </button>
            </div>
          )}
        </div>

        <div className="admin-notes-content">
          {isEditingNotes ? (
            <textarea
              value={adminNotes}
              onChange={(e) => setAdminNotes(e.target.value)}
              placeholder={t('admin.notesPlaceholder')}
              className="admin-notes-textarea"
              rows={6}
              maxLength={1000}
            />
          ) : (
            <div className="admin-notes-display">
              {profileData.adminNotes ? (
                <div className="admin-notes-text">
                  {profileData.adminNotes}
                </div>
              ) : (
                <div className="admin-notes-empty">
                  <em>{t('admin.noNotes')}</em>
                </div>
              )}
            </div>
          )}
          
          {isEditingNotes && (
            <div className="admin-notes-counter">
              {adminNotes.length}/1000 {t('admin.characters')}
            </div>
          )}
        </div>
      </div>

      {/* Full Profile Display */}
      <div className="admin-profile-content">
        <div className="admin-profile-section">
          <h2>{t('admin.fullProfileInformation')}</h2>
          <p className="admin-profile-note">
            {t('admin.profileNote')}
          </p>
        </div>
        
        <div className="profile-sections">
          {/* Business Information - First, matches questionnaire */}
          {profileData.businesses && profileData.businesses.length > 0 ? (
            <>
              {profileData.businesses.map((business: any, index: number) => (
                <div key={index} className="profile-section">
                  <h2 className="section-title">{tDashboard('profile.businessInformation')} {profileData.businesses.length > 1 ? index + 1 : ''}</h2>
                  <div className="profile-fields">
                    <div className="profile-field">
                      <label>{tDashboard('profile.businessName')}</label>
                      <div className="field-value">{business.businessName || tDashboard('profile.notProvided')}</div>
                    </div>
                    <div className="profile-field">
                      <label>{tDashboard('profile.businessCategory')}</label>
                      <div className="field-value">
                        {(() => {
                          const cats = business.businessCategory;
                          if (Array.isArray(cats) && cats.length > 0) {
                            return cats.map((c: string) => tBusinessCategory(c) || c).join(', ');
                          }
                          if (typeof cats === 'string' && cats.trim()) {
                            return tBusinessCategory(cats) || cats;
                          }
                          return tDashboard('profile.notProvided');
                        })()}
                      </div>
                    </div>
                    <div className="profile-field">
                      <label>{tDashboard('profile.countriesOfOperation')}</label>
                      <div className="field-value">
                        {business.countriesOfOperation && business.countriesOfOperation.length > 0 
                          ? business.countriesOfOperation.map(country => tDashboard(`profile.countries.${country}`)).join(', ') 
                          : tDashboard('profile.notSpecified')}
                      </div>
                    </div>
                    <div className="profile-field">
                      <label>{tDashboard('profile.businessWebsite')}</label>
                      <div className="field-value">
                        {business.businessWebsite ? (
                          <a href={business.businessWebsite} target="_blank" rel="noopener noreferrer">
                            {business.businessWebsite}
                          </a>
                        ) : tDashboard('profile.noWebsite')}
                      </div>
                    </div>
                    <div className="profile-field">
                      <label>{tDashboard('profile.companyPhoneNumber')}</label>
                      <div className="field-value">
                        {(() => {
                          let cc = (business.companyPhoneCountryCode || "").replace(/[\s\-().]/g, "").trim();
                          let pn = (business.companyPhoneNumber || "").replace(/[\s\-().]/g, "").trim();
                          if (!pn) return tDashboard('profile.notProvided');
                          if (!cc) cc = "+44";
                          if (!cc.startsWith("+")) cc = `+${cc}`;
                          const ccDigits = cc.replace("+", "");
                          if (pn.startsWith(cc)) pn = pn.substring(cc.length);
                          else if (pn.startsWith(`+${ccDigits}`)) pn = pn.substring(ccDigits.length + 1);
                          else if (pn.startsWith(ccDigits) && pn.length > ccDigits.length + 4) pn = pn.substring(ccDigits.length);
                          if (cc === "+44" && pn.startsWith("0")) pn = pn.substring(1);
                          pn = pn.replace(/[\s\-()]/g, "");
                          return pn ? `${cc} ${pn}` : tDashboard('profile.notProvided');
                        })()}
                      </div>
                    </div>
                    <div className="profile-field">
                      <label>{tDashboard('profile.businessDescription')}</label>
                      <div className="field-value">{business.businessDescription || tDashboard('profile.notProvided')}</div>
                    </div>
                    <div className="profile-field">
                      <label>{tDashboard('profile.socialMedia')}</label>
                      <div className="field-value">{business.socialMedia || tDashboard('profile.notProvided')}</div>
                    </div>
                  </div>
                </div>
              ))}
            </>
          ) : (
            <div className="profile-section">
              <h2 className="section-title">{tDashboard('profile.businessInformation')}</h2>
              <div className="profile-fields">
                <div className="profile-field">
                  <label>{tDashboard('profile.businessName')}</label>
                  <div className="field-value">{tDashboard('profile.notProvided')}</div>
                </div>
                <div className="profile-field">
                  <label>{tDashboard('profile.businessCategory')}</label>
                  <div className="field-value">{tDashboard('profile.notProvided')}</div>
                </div>
                <div className="profile-field">
                  <label>{tDashboard('profile.countriesOfOperation')}</label>
                  <div className="field-value">{tDashboard('profile.notSpecified')}</div>
                </div>
                <div className="profile-field">
                  <label>{tDashboard('profile.businessWebsite')}</label>
                  <div className="field-value">{tDashboard('profile.noWebsite')}</div>
                </div>
                <div className="profile-field">
                  <label>{tDashboard('profile.businessDescription')}</label>
                  <div className="field-value">{tDashboard('profile.notProvided')}</div>
                </div>
                <div className="profile-field">
                  <label>{tDashboard('profile.socialMedia')}</label>
                  <div className="field-value">{tDashboard('profile.notProvided')}</div>
                </div>
              </div>
            </div>
          )}

          {/* Interests - Second, matches questionnaire */}
          <div className="profile-section">
            <h2 className="section-title">{tDashboard('profile.interests')}</h2>
            <div className="profile-fields">
              <div className="profile-field">
                <div className="field-value">
                  {profileData.interestedIn && profileData.interestedIn.length > 0 
                    ? translatePosition(profileData.interestedIn, tDashboard, "profile.interestedInOptions", tDashboardExists) 
                    : tDashboard('profile.notSpecified')}
                </div>
              </div>
            </div>
          </div>

          {/* Personal Information - Third, matches questionnaire */}
          <div className="profile-section">
            <h2 className="section-title">{tDashboard('profile.personalInformation')}</h2>
            <div className="profile-fields">
              <div className="profile-field">
                <label>{tDashboard('profile.memberId')}</label>
                <div className="field-value">{profileData._id || tDashboard('profile.notProvided')}</div>
              </div>
              <div className="profile-field">
                <label>{tAuth('name')}</label>
                <div className="field-value">{profileData.name || tDashboard('profile.notProvided')}</div>
              </div>
              <div className="profile-field">
                <label>{tAuth('surname')}</label>
                <div className="field-value">{profileData.surname || tDashboard('profile.notProvided')}</div>
              </div>
              <div className="profile-field">
                <label>{tAuth('phoneNumber')}</label>
                <div className="field-value">
                  {(() => {
                    let cc = (profileData.countryCode || "").trim();
                    let pn = (profileData.phoneNumber || "").trim();
                    if (!pn) return tDashboard('profile.notProvided');
                    if (!cc) cc = "+44";
                    if (!cc.startsWith("+")) cc = `+${cc}`;
                    const ccDigits = cc.replace("+", "");
                    if (pn.startsWith(cc)) pn = pn.substring(cc.length).trim();
                    else if (pn.startsWith(`+${ccDigits}`)) pn = pn.substring(ccDigits.length + 1).trim();
                    else if (pn.startsWith(ccDigits) && pn.length > ccDigits.length + 4) pn = pn.substring(ccDigits.length).trim();
                    if (cc === "+44" && pn.startsWith("0")) pn = pn.substring(1).trim();
                    pn = pn.replace(/[\s\-()]/g, "");
                    return pn ? `${cc} ${pn}` : tDashboard('profile.notProvided');
                  })()}
                </div>
              </div>
              <div className="profile-field">
                <label>{tAuth('email')}</label>
                <div className="field-value">{profileData.email || tDashboard('profile.notProvided')}</div>
              </div>
              <div className="profile-field">
                <label>{tDashboard('profile.position')}</label>
                <div className="field-value">
                  {translatePosition(profileData.position, tDashboard, "profile.positionOptions", tDashboardExists) || tDashboard('profile.notProvided')}
                </div>
              </div>
              <div className="profile-field">
                <label>{tDashboard('profile.linkedin')}</label>
                <div className="field-value">
                  {profileData.linkedin ? (
                    <a href={profileData.linkedin} target="_blank" rel="noopener noreferrer">
                      {profileData.linkedin}
                    </a>
                  ) : tDashboard('profile.notProvided')}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default B2BAdminProfileView;
